import {EventEmitter, Injectable} from '@angular/core';


import {catchError, map} from 'rxjs/operators';
import {firstValueFrom, throwError} from 'rxjs';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {ApiErrorResponse, ApiService} from '../api/api.service';
import {LocalStorage} from '../../utils/storage';
import {User} from '../../classes/user';
import {AuthResponse} from '../../classes/auth-response';
import {Routenames} from '../../routenames';
import {AuthenticationLog} from '../../classes/authenticationlog';
import {Utils} from '../../utils.class';
import {ConfirmDialogService} from '../confirm-dialog-service/confirm-dialog.service';

/**
 * valid: token is bruikbaar (niet stale of succesvol ververst)
 * retry: verversen lukte niet door bijv. geen verbinding, een volgende check probeert opnieuw
 * loggedOut: token is niet meer te verversen, gebruiker wordt uitgelogd
 */
export type TokenCheckResult = 'valid' | 'retry' | 'loggedOut';

@Injectable()
export class AuthenticationService {

    static filledInEmail = '';

    private static redirect: string;
    public loggedIn = new EventEmitter<boolean>();
    onLogin = this.loggedIn.asObservable();

    private tokenRefreshPending: Promise<TokenCheckResult> = null;

    constructor(private apiService: ApiService,
                private matDialog: MatDialog,
                private router: Router,
                private confirmDialogService: ConfirmDialogService) {
    }

    getRedirect() {
        return AuthenticationService.redirect;
    }

    randomPassword(userId: number) {
        return this.apiService.getCall$<{ password: string; }>(`auth/randomPassword/${userId}`);
    }

    setRedirect(redirect) {
        AuthenticationService.redirect = redirect;
    }

    getCurrentUser() {
        return this.apiService.getCall$<User>('auth/me')
            .pipe(map(response => LocalStorage.user = response.data));
    }


    switchToParent() {
        const parentToken = LocalStorage.getParentUserToken();
        LocalStorage.logoutUser();
        LocalStorage.unsetParentUser();
        this.refresh(parentToken).subscribe(() => {
            this.router.navigate([Routenames.Home], {replaceUrl: true});
        }, () => {
            LocalStorage.unsetParentUser();
            this.router.navigate([Routenames.Logout], {replaceUrl: true});
        });
    }

    switchToProfile(token) {
        const parentToken = LocalStorage.userToken;
        const parentUser = LocalStorage.user;
        LocalStorage.logoutUser();
        LocalStorage.setParentUser(parentToken, parentUser);
        return this.refresh(token);
    }

    loginEmail(email) {
        return this.apiService.postCall$<boolean>('auth/login-email', {
            email,
            redirect: AuthenticationService.redirect
        });
    }

    loginCode(email) {
        return this.apiService.postCall$<boolean>('auth/login-code', {
            email,
            redirect: AuthenticationService.redirect
        })
    }

    validateCode(email, pin) {
        return this.apiService.postCall$<AuthResponse>('auth/validate-code', {
            pin,
            email,
            redirect: AuthenticationService.redirect
        }, true)
            .pipe(map(response => this.handleResponse(response.data as AuthResponse)));
    }

    login(email, password) {
        return this.apiService.postCall$<AuthResponse>('auth/login', {
            email,
            password,
            redirect: AuthenticationService.redirect,
            twoFaHash: LocalStorage.twoFaHash
        })
            .pipe(map(response => this.handleResponse(response.data as AuthResponse)));
    }

    refresh(token?: string, noErrorDialogs = false) {
        if (token) {
            return this.apiService.postCall$<AuthResponse>('auth/refresh', {token, twoFaHash: LocalStorage.twoFaHash}, noErrorDialogs)
                .pipe(map(response => this.handleResponse(response.data as AuthResponse)));
        }
        return this.apiService.getCall$<AuthResponse>('auth/refresh', {twoFaHash: LocalStorage.twoFaHash}, noErrorDialogs, true)
            .pipe(map(response => this.handleResponse(response.data)));
    }

    /**
     * Ververst het token zodra de helft van de levensduur voorbij is. Wordt aangeroepen bij opstarten,
     * navigatie, een interval en terugkeren naar de app in plaats van één lange timer, omdat timers in
     * achtergrondtabs en een slapende PWA niet betrouwbaar afgaan.
     */
    checkToken(): Promise<TokenCheckResult> {
        // checkToken draait bij elke navigatie; zonder deze guard start elke aanroep een eigen refresh
        if (this.tokenRefreshPending) {
            return this.tokenRefreshPending;
        }
        if (!this.isTokenStale()) {
            return Promise.resolve('valid');
        }

        // Opnieuw controleren binnen de lock: een andere tab kan tijdens het wachten al ververst hebben
        const refresh = async (): Promise<ApiErrorResponse | null> => {
            const tokenBefore = LocalStorage.userToken;
            if (!this.isTokenStale()) {
                return null;
            }
            try {
                if (!await firstValueFrom(this.refresh(null, true))) {
                    throw new ApiErrorResponse();
                }
                return null;
            } catch (error) {
                // Zonder locks kan een andere tab het token al vervangen hebben; een token is maar één keer te verversen
                if (LocalStorage.userToken !== tokenBefore && !this.isTokenStale()) {
                    return null;
                }
                return error;
            }
        };

        // Een verlopen token direct verversen, anders tabs met een random vertraging spreiden
        const delay = this.isTokenExpired() ? 0 : (2 + Math.random() * 3) * 1000;
        // De lock serialiseert tabs; niet beschikbaar in onveilige contexten en oude browsers
        this.tokenRefreshPending = new Promise<ApiErrorResponse | null>(resolve => {
            setTimeout(() => resolve(navigator.locks ? navigator.locks.request('token-refresh', refresh) : refresh()), delay);
        })
            .then(error => error ? this.handleRefreshError(error) : 'valid' as TokenCheckResult)
            .finally(() => this.tokenRefreshPending = null);
        return this.tokenRefreshPending;
    }

    private isTokenStale() {
        const token = LocalStorage.userToken;
        if (!token) {
            return false;
        }
        try {
            // The lifetime of this token starts at nbf, the moment it was issued. iat is not usable for that: a refresh
            // can keep the iat of the first login, and then every refreshed token counted as stale right away.
            const {iat, nbf, exp} = Utils.getJwtData(token);
            const issuedAt = nbf ?? iat;
            return Date.now() / 1000 >= issuedAt + (exp - issuedAt) / 2;
        } catch (e) {
            return true;
        }
    }

    isTokenExpired() {
        const token = LocalStorage.userToken;
        if (!token) {
            return false;
        }
        try {
            return Utils.getJwtData(token).exp * 1000 <= Date.now();
        } catch (e) {
            return true;
        }
    }

    private async handleRefreshError(error: ApiErrorResponse): Promise<TokenCheckResult> {
        // Geen verbinding (bijv. PWA offline): niet uitloggen, de volgende check probeert opnieuw
        if (!error?.status) {
            return 'retry';
        }
        // Bij 500 toont de CodaltInterceptor al een dialoog
        if (error.status !== 500 && error.message) {
            await this.confirmDialogService.confirm('Uitgelogd', error.message, 'Opnieuw inloggen', null).catch(() => null);
        }
        this.router.navigateByUrl(Routenames.Logout);
        return 'loggedOut';
    }

    logout() {
        this.matDialog.closeAll();
        return this.apiService.postCall$('auth/logout')
            .pipe(map(response => {
                LocalStorage.logoutUser();
                this.loggedIn.emit(false);
                return response;
            }), catchError(error => {
                LocalStorage.logoutUser();
                this.loggedIn.emit(false);
                return throwError(error);
            }));
    }

    requestRestorePassword(email) {
        return this.apiService.postCall$<AuthResponse>('auth/request-restore', {email});
    }

    verifyResetToken(token: string) {
        return this.apiService.getCall$('auth/verify-reset-token', {token});
    }

    restorePassword(token: string, password: string) {
        return this.apiService.postCall$<AuthResponse>('auth/restore', {password, token})
            .pipe(map(authResponse => this.handleResponse(authResponse.data as AuthResponse)));
    }

    authLog(user_id: number) {
        return this.apiService.getCall$<AuthenticationLog[]>(`auth/log/${user_id}`);
    }

    loginAsUser(userId: number) {
        return this.apiService.postCall$<string>('auth/loginAsUser', {user_id: userId});
    }

    private handleResponse(authResponse: AuthResponse) {
        if (authResponse.redirect) {
            this.setRedirect(authResponse.redirect);
        }
        if (!!authResponse.access_token) {
            LocalStorage.userToken = authResponse.access_token;
            LocalStorage.user = authResponse.user;
            if (authResponse.needsOtp) {
                if (LocalStorage.user.hasOtp) {
                    this.router.navigate([Routenames.Twofactor]);
                } else {
                    this.router.navigateByUrl(`${Routenames.TwofactorSetup}`);
                }
            }
            this.loggedIn.emit(true);
            return authResponse;
        } else {
            return null;
        }
    }
}
